import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Loader } from './shared';
import Select from './shared/Select';

const TranscriptionService = ({ service, isOnline, status }) => {
  // Existing state
  const [file, setFile] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState('');
  const [task, setTask] = useState('transcribe');
  const [inputMode, setInputMode] = useState('file'); // 'file' or 'youtube'
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [cookiesStatus, setCookiesStatus] = useState(null);
  const [uploadingCookies, setUploadingCookies] = useState(false);
  const [showCookiesUpload, setShowCookiesUpload] = useState(false);

  // New state for tabs and job history
  const [activeTab, setActiveTab] = useState('transcribe'); // 'transcribe' or 'history'
  const [jobHistory, setJobHistory] = useState([]);
  const [jobStats, setJobStats] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [jobFilter, setJobFilter] = useState('all'); // 'all', 'file_upload', 'youtube'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'completed', 'failed'
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all', 'web_interface', or specific service name
  const [expandedTranscripts, setExpandedTranscripts] = useState({}); // Track which transcripts are expanded
  const [serverTimeRef, setServerTimeRef] = useState(null); // Server timestamp reference { serverTime, fetchedAt }
  const [timerTick, setTimerTick] = useState(0); // Counter to trigger re-renders for timer updates

  const supportedFormats = ['mp3', 'mp4', 'wav', 'm4a', 'flac', 'ogg', 'webm', 'avi', 'mov', 'mkv'];
  const maxFileSize = 100; // MB

  // Check cookies status when switching to YouTube mode
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (inputMode === 'youtube' && isOnline) {
      checkCookiesStatus();
    }
  }, [inputMode, isOnline]);

  // Fetch job history when tab changes to history
  useEffect(() => {
    if (activeTab === 'history' && isOnline) {
      fetchJobHistory();
      fetchJobStats();
    }
  }, [activeTab, isOnline, jobFilter, statusFilter, sourceFilter]);

  // Auto-refresh for processing jobs + timer updates
  useEffect(() => {
    if (activeTab !== 'history' || !isOnline) return;

    // Check if there are any processing jobs
    const hasProcessingJobs = jobHistory.some(job => job.status === 'processing');

    if (hasProcessingJobs) {
      // Update timer every second
      const timerInterval = setInterval(() => {
        setTimerTick(prev => prev + 1);
      }, 1000);

      // Refresh job list every 10 seconds
      const refreshInterval = setInterval(() => {
        fetchJobHistory();
      }, 10000);

      return () => {
        clearInterval(timerInterval);
        clearInterval(refreshInterval);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isOnline, jobHistory]);

  const fetchJobHistory = async () => {
    setLoadingHistory(true);
    setHistoryError(null);

    try {
      const params = new URLSearchParams({
        limit: 50,
        offset: 0
      });

      if (jobFilter !== 'all') {
        params.append('job_type', jobFilter);
      }

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await fetch(`${service.url}/job-history?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${service.apiKey}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch job history: ${response.status}`);
      }

      const data = await response.json();
      let jobs = data.jobs || [];

      // Client-side filtering by source_service (since API doesn't support it)
      if (sourceFilter !== 'all') {
        jobs = jobs.filter(job => job.source_service === sourceFilter);
      }

      // Capture server timestamp as reference point for timer calculations
      if (data.server_timestamp) {
        setServerTimeRef({
          serverTime: new Date(data.server_timestamp + 'Z').getTime(), // Parse as UTC
          fetchedAt: Date.now() // Client time when we fetched this
        });
      }

      setJobHistory(jobs);
    } catch (err) {
      console.error('Failed to fetch job history:', err);
      setHistoryError(err.message);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchJobStats = async () => {
    try {
      const response = await fetch(`${service.url}/job-stats`, {
        headers: {
          'Authorization': `Bearer ${service.apiKey}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch job stats: ${response.status}`);
      }

      const data = await response.json();
      setJobStats(data);
    } catch (err) {
      console.error('Failed to fetch job stats:', err);
    }
  };

  const checkCookiesStatus = async () => {
    try {
      const response = await fetch(`${service.url}/youtube-cookies-status`, {
        headers: {
          'Authorization': `Bearer ${service.apiKey}`
        }
      });
      const data = await response.json();
      setCookiesStatus(data);
    } catch (err) {
      console.error('Failed to check cookies status:', err);
    }
  };

  const toggleTranscript = (jobId) => {
    setExpandedTranscripts(prev => ({
      ...prev,
      [jobId]: !prev[jobId]
    }));
  };

  const copyTranscript = (transcript) => {
    navigator.clipboard.writeText(transcript).then(() => {
      alert('Transcript copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy transcript:', err);
      alert('Failed to copy transcript to clipboard');
    });
  };

  // Calculate elapsed time and ETA for processing jobs
  const getProcessingInfo = (job) => {
    if (job.status !== 'processing') return null;

    // Parse job created_at timestamp as UTC (database stores format: 2025-10-08T16:37:29.683035)
    const timestamp = job.created_at.replace(/\.\d+$/, ''); // Remove microseconds if present
    const [datePart, timePart] = timestamp.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second] = timePart.split(':').map(Number);
    const createdAtUtc = Date.UTC(year, month - 1, day, hour, minute, second);

    // Calculate elapsed time using server time reference to avoid client clock issues
    let elapsedMs;
    if (serverTimeRef) {
      // Time elapsed on server when we fetched + time elapsed on client since fetch
      const serverElapsedAtFetch = serverTimeRef.serverTime - createdAtUtc;
      const clientElapsedSinceFetch = Date.now() - serverTimeRef.fetchedAt;
      elapsedMs = serverElapsedAtFetch + clientElapsedSinceFetch;
    } else {
      // Fallback: use client time (will be wrong if client clock is incorrect)
      elapsedMs = Date.now() - createdAtUtc;
    }

    const elapsedSeconds = Math.floor(elapsedMs / 1000);

    // Safety check for negative time (shouldn't happen with UTC parsing)
    if (elapsedSeconds < 0) {
      return {
        elapsed: '0m 0s',
        eta: 'Calculating...',
        progress: 0
      };
    }

    const elapsedMinutes = Math.floor(elapsedSeconds / 60);
    const remainingSeconds = elapsedSeconds % 60;

    // Estimate ETA based on file size
    // Average: ~1 minute per 10MB for base model
    const fileSizeMB = job.file_size ? job.file_size / (1024 * 1024) : 0;
    const estimatedTotalSeconds = fileSizeMB * 6; // 6 seconds per MB (conservative estimate)
    const remainingEstimatedSeconds = Math.max(0, estimatedTotalSeconds - elapsedSeconds);
    const etaMinutes = Math.floor(remainingEstimatedSeconds / 60);
    const etaSeconds = Math.floor(remainingEstimatedSeconds % 60);

    return {
      elapsed: `${elapsedMinutes}m ${remainingSeconds}s`,
      eta: remainingEstimatedSeconds > 0 ? `~${etaMinutes}m ${etaSeconds}s` : 'Almost done...',
      progress: estimatedTotalSeconds > 0 ? Math.min(95, (elapsedSeconds / estimatedTotalSeconds) * 100) : 0
    };
  };

  const handleCookiesUpload = async (event) => {
    const cookiesFile = event.target.files[0];
    if (!cookiesFile) return;

    setUploadingCookies(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', cookiesFile);

      const response = await fetch(`${service.url}/upload-youtube-cookies`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${service.apiKey}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload cookies');
      }

      await checkCookiesStatus();
      setShowCookiesUpload(false);
      setError(null);
    } catch (err) {
      setError(`Cookies upload failed: ${err.message}`);
    } finally {
      setUploadingCookies(false);
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // Check file size
      const fileSizeMB = selectedFile.size / (1024 * 1024);
      if (fileSizeMB > maxFileSize) {
        setError(`File size must be less than ${maxFileSize}MB`);
        return;
      }

      // Check file format
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (!supportedFormats.includes(fileExtension)) {
        setError(`Unsupported file format. Supported formats: ${supportedFormats.join(', ')}`);
        return;
      }

      setFile(selectedFile);
      setError(null);
      setResult(null);
    }
  };

  const handleTranscribe = async () => {
    if (inputMode === 'file' && !file) return;
    if (inputMode === 'youtube' && !youtubeUrl.trim()) return;
    if (!isOnline) return;

    setTranscribing(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();

      if (inputMode === 'file') {
        formData.append('file', file);
        formData.append('task', task);
        if (language && language.trim() !== '') {
          formData.append('language', language);
        }

        const response = await fetch(`${service.url}/transcribe`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${service.apiKey}`
          },
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setResult(data);
      } else if (inputMode === 'youtube') {
        formData.append('url', youtubeUrl.trim());
        formData.append('task', task);
        if (language && language.trim() !== '') {
          formData.append('language', language);
        }

        const response = await fetch(`${service.url}/transcribe-youtube`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${service.apiKey}`
          },
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setResult(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setTranscribing(false);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  if (!isOnline) {
    const isLoading = status === 'checking' || status === 'starting' || status === 'initializing';
    return (
      <Card style={{
        backgroundColor: '#FFFFFF',
        border: isLoading ? '1px solid #e0e0e0' : '1px solid #e0e0e0'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          <div style={{ flexShrink: 0, marginRight: '12px' }}>
            {isLoading ? (
              <Loader size="sm" />
            ) : (
              <svg style={{ height: '20px', width: '20px', color: '#000000' }} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: '14px', fontWeight: 500, color: '#000000', margin: 0 }}>
              {status === 'checking' ? 'Checking Service...' :
               status === 'starting' ? 'Service Starting...' :
               status === 'initializing' ? 'Service Initializing...' :
               'Service Offline'}
            </h3>
            <p style={{ fontSize: '13px', color: 'rgba(0, 0, 0, 0.6)', marginTop: '4px', marginBottom: 0 }}>
              {status === 'checking' ? 'Checking if the transcription service is available...' :
               status === 'starting' ? 'The transcription service is starting up. This may take a few moments.' :
               status === 'initializing' ? 'The transcription service is loading AI models in the background. You can try using it now.' :
               'The transcription service is currently unavailable. Please check the service status.'}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Tabs */}
      <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('transcribe')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'transcribe'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Transcribe
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'history'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Job History
        </button>
      </div>

      {/* Transcribe Tab Content */}
      {activeTab === 'transcribe' && (
        <div className="space-y-6">
          {/* Input Mode Toggle */}
          <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => {
                setInputMode('file');
                setError(null);
                setResult(null);
              }}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                inputMode === 'file'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Upload File
            </button>
            <button
              onClick={() => {
                setInputMode('youtube');
                setError(null);
                setResult(null);
              }}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                inputMode === 'youtube'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              YouTube URL
            </button>
          </div>

          {/* Upload Section - File */}
          {inputMode === 'file' && (
            <label htmlFor="file-upload" className="block cursor-pointer">
              <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 hover:bg-gray-100 transition-colors">
                <div className="text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="mt-4">
                    <span className="mt-2 block text-sm font-medium text-gray-900">
                      {file ? file.name : 'Choose audio or video file'}
                    </span>
                    <p className="text-sm text-gray-500 mt-1">
                      Supported formats: {supportedFormats.join(', ')} (max {maxFileSize}MB)
                    </p>
                  </div>
                </div>
              </div>
              <input
                id="file-upload"
                name="file-upload"
                type="file"
                className="sr-only"
                accept={supportedFormats.map(fmt => `.${fmt}`).join(',')}
                onChange={handleFileChange}
              />
            </label>
          )}

          {/* YouTube URL Section */}
          {inputMode === 'youtube' && (
            <>
              <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6">
                <div className="text-center mb-4">
                  <svg className="mx-auto h-12 w-12 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  <div className="mt-4">
                    <span className="block text-sm font-medium text-gray-900 mb-2">
                      Enter YouTube Video URL
                    </span>
                    <Input
                      type="text"
                      value={youtubeUrl}
                      onChange={(e) => setYoutubeUrl(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                      fullWidth
                    />
                    <p className="text-sm text-gray-500 mt-2">
                      The audio will be downloaded and transcribed automatically
                    </p>
                  </div>
                </div>
              </div>

              {/* YouTube Authentication Notice */}
              <div className={`border rounded-lg p-4 ${cookiesStatus?.status === 'configured' ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start flex-1">
                    <svg className={`h-5 w-5 mt-0.5 mr-3 flex-shrink-0 ${cookiesStatus?.status === 'configured' ? 'text-green-400' : 'text-yellow-400'}`} fill="currentColor" viewBox="0 0 20 20">
                      {cookiesStatus?.status === 'configured' ? (
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      ) : (
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      )}
                    </svg>
                    <div className="flex-1">
                      <h3 className={`text-sm font-medium mb-1 ${cookiesStatus?.status === 'configured' ? 'text-green-800' : 'text-yellow-800'}`}>
                        {cookiesStatus?.status === 'configured' ? 'YouTube Authentication Configured' : 'YouTube Authentication Recommended'}
                      </h3>
                      <p className={`text-sm mb-2 ${cookiesStatus?.status === 'configured' ? 'text-green-700' : 'text-yellow-700'}`}>
                        {cookiesStatus?.status === 'configured'
                          ? 'YouTube cookies are configured. You can now transcribe YouTube videos reliably.'
                          : 'Upload YouTube cookies to enable reliable video downloads. Without cookies, many videos will fail due to bot detection.'}
                      </p>
                      {!showCookiesUpload && cookiesStatus?.status !== 'configured' && (
                        <Button
                          onClick={() => setShowCookiesUpload(true)}
                          variant="primary"
                          size="sm"
                        >
                          Upload Cookies File
                        </Button>
                      )}
                      {showCookiesUpload && (
                        <div className="mt-2 space-y-2">
                          <input
                            type="file"
                            accept=".txt"
                            onChange={handleCookiesUpload}
                            disabled={uploadingCookies}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          />
                          <button
                            onClick={() => setShowCookiesUpload(false)}
                            className="text-sm text-gray-600 hover:text-gray-800"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                      <details className={`text-sm mt-2 ${cookiesStatus?.status === 'configured' ? 'text-green-600' : 'text-yellow-600'}`}>
                        <summary className="cursor-pointer hover:underline font-medium">How to get cookies</summary>
                        <ol className={`list-decimal list-inside mt-2 space-y-1 ${cookiesStatus?.status === 'configured' ? 'text-green-700' : 'text-yellow-700'}`}>
                          <li>Install "Get cookies.txt LOCALLY" browser extension</li>
                          <li>Visit YouTube.com while logged in</li>
                          <li>Click extension icon and export cookies.txt</li>
                          <li>Upload the file using the button above</li>
                        </ol>
                      </details>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Options */}
          {(file || youtubeUrl.trim()) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  type="text"
                  id="language"
                  label="Language (optional)"
                  placeholder="e.g., en, es, fr"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  helperText="Leave empty for auto-detection"
                  fullWidth
                />
              </div>
              <div>
                <Select
                  label="Task"
                  value={task}
                  onChange={(e) => setTask(e.target.value)}
                  options={[
                    { value: 'transcribe', label: 'Transcribe' },
                    { value: 'translate', label: 'Translate to English' }
                  ]}
                  fullWidth
                />
              </div>
            </div>
          )}

          {/* Action Button */}
          {(file || youtubeUrl.trim()) && (
            <Button
              onClick={handleTranscribe}
              disabled={transcribing}
              variant="primary"
              style={{ width: '100%', padding: '10px 16px' }}
            >
              {transcribing ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Loader size="sm" text="Transcribing..." />
                </div>
              ) : (
                'Start Transcription'
              )}
            </Button>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-red-800 mb-1">Transcription Error</h3>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Results Display */}
          {result && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 space-y-4">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <h3 className="text-lg font-medium text-green-800">Transcription Complete</h3>
              </div>

              {/* Metadata */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="font-medium text-green-800">Language:</span>
                  <p className="text-green-700">{result.language}</p>
                </div>
                <div>
                  <span className="font-medium text-green-800">Duration:</span>
                  <p className="text-green-700">{formatTime(result.duration)}</p>
                </div>
                <div>
                  <span className="font-medium text-green-800">Processing Time:</span>
                  <p className="text-green-700">{result.processing_time?.toFixed(1)}s</p>
                </div>
                <div>
                  <span className="font-medium text-green-800">Model:</span>
                  <p className="text-green-700">{result.model_used}</p>
                </div>
              </div>

              {/* Transcription Text */}
              <div>
                <h4 className="font-medium text-green-800 mb-2">Transcription:</h4>
                <div className="bg-white border border-green-200 rounded p-4">
                  <p className="text-gray-900 whitespace-pre-wrap">{result.text}</p>
                </div>
              </div>

              {/* Segments (if available) */}
              {result.segments && result.segments.length > 0 && (
                <div>
                  <h4 className="font-medium text-green-800 mb-2">Detailed Segments:</h4>
                  <div className="bg-white border border-green-200 rounded max-h-64 overflow-y-auto">
                    {result.segments.map((segment, index) => (
                      <div key={index} className="p-3 border-b border-green-100 last:border-b-0">
                        <div className="flex justify-between items-start mb-1">
                          <span className="text-xs text-gray-500 font-medium">
                            {formatTime(segment.start)} - {formatTime(segment.end)}
                          </span>
                        </div>
                        <p className="text-gray-900 text-sm">{segment.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Copy Button */}
              <Button
                onClick={() => navigator.clipboard.writeText(result.text)}
                variant="success"
                size="sm"
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <svg style={{ marginRight: '8px', height: '16px', width: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy Text
                </div>
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Job History Tab Content */}
      {activeTab === 'history' && (
        <div className="space-y-6">
          {/* Stats Summary */}
          {jobStats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-sm font-medium text-blue-800">Total Jobs</div>
                <div className="text-2xl font-bold text-blue-900 mt-1">{jobStats.total_jobs || 0}</div>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="text-sm font-medium text-green-800">Completed</div>
                <div className="text-2xl font-bold text-green-900 mt-1">{jobStats.by_status?.completed || 0}</div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="text-sm font-medium text-red-800">Failed</div>
                <div className="text-2xl font-bold text-red-900 mt-1">{jobStats.by_status?.failed || 0}</div>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="text-sm font-medium text-purple-800">Avg Processing</div>
                <div className="text-2xl font-bold text-purple-900 mt-1">
                  {jobStats.average_processing_time ? `${jobStats.average_processing_time.toFixed(1)}s` : 'N/A'}
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Select
                label="Job Type"
                value={jobFilter}
                onChange={(e) => setJobFilter(e.target.value)}
                options={[
                  { value: 'all', label: 'All Types' },
                  { value: 'file_upload', label: 'File Upload' },
                  { value: 'youtube', label: 'YouTube' }
                ]}
                fullWidth
              />
            </div>
            <div>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'failed', label: 'Failed' },
                  { value: 'processing', label: 'Processing' }
                ]}
                fullWidth
              />
            </div>
            <div>
              <Select
                label="Source"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                options={[
                  { value: 'all', label: 'All Sources' },
                  { value: 'web_interface', label: 'Web Interface' },
                  { value: 'flo_os', label: 'flo_os' },
                  { value: 'project-management', label: 'Project Management' },
                  { value: 'automation', label: 'Automation' },
                  { value: 'testing-service', label: 'Testing Service' }
                ]}
                fullWidth
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={() => {
                  fetchJobHistory();
                  fetchJobStats();
                }}
                variant="primary"
                disabled={loadingHistory}
                style={{ width: '100%' }}
              >
                {loadingHistory ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>
          </div>

          {/* Error Display */}
          {historyError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-red-800 mb-1">Error Loading History</h3>
                  <p className="text-sm text-red-700">{historyError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loadingHistory && (
            <div className="text-center py-12">
              <Loader size="md" text="Loading job history..." />
            </div>
          )}

          {/* Job History List */}
          {!loadingHistory && !historyError && jobHistory.length === 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-sm font-medium text-gray-900 mb-1">No jobs found</h3>
              <p className="text-sm text-gray-500">
                {jobFilter !== 'all' || statusFilter !== 'all'
                  ? 'Try adjusting your filters to see more results.'
                  : 'Start transcribing files or YouTube videos to see job history here.'}
              </p>
            </div>
          )}

          {!loadingHistory && !historyError && jobHistory.length > 0 && (
            <div className="space-y-3">
              {jobHistory.map((job) => (
                <div
                  key={job.id}
                  className={`border rounded-lg p-4 ${
                    job.status === 'completed' ? 'bg-green-50 border-green-200' :
                    job.status === 'failed' ? 'bg-red-50 border-red-200' :
                    'bg-yellow-50 border-yellow-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          job.status === 'completed' ? 'bg-green-100 text-green-800' :
                          job.status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {job.status}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          job.job_type === 'youtube' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {job.job_type === 'youtube' ? 'YouTube' : 'File Upload'}
                        </span>
                        {job.source_service && job.source_service !== 'web_interface' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            <i className="fas fa-robot mr-1"></i>
                            {job.source_service}
                          </span>
                        )}
                      </div>

                      <h4 className="text-sm font-medium text-gray-900 mb-1">
                        {job.filename || 'Unknown source'}
                      </h4>

                      {/* Display YouTube URL for YouTube jobs */}
                      {job.job_type === 'youtube' && job.source_url && (
                        <a
                          href={job.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline break-all"
                        >
                          <i className="fas fa-external-link-alt mr-1"></i>
                          {job.source_url}
                        </a>
                      )}

                      {/* Processing Timer and ETA */}
                      {job.status === 'processing' && (() => {
                        const procInfo = getProcessingInfo(job);
                        return procInfo ? (
                          <div className="mb-3">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-gray-600">
                                <i className="fas fa-clock mr-1"></i>
                                Elapsed: <span className="font-medium text-gray-900">{procInfo.elapsed}</span>
                              </span>
                              <span className="text-gray-600">
                                ETA: <span className="font-medium text-gray-900">{procInfo.eta}</span>
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-yellow-500 h-2 rounded-full transition-all duration-1000"
                                style={{ width: `${procInfo.progress}%` }}
                              ></div>
                            </div>
                          </div>
                        ) : null;
                      })()}

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-600">
                        {job.created_at && (
                          <div>
                            <span className="font-medium">Created:</span> {formatDate(job.created_at)}
                          </div>
                        )}
                        {job.language && (
                          <div>
                            <span className="font-medium">Language:</span> {job.language}
                          </div>
                        )}
                        {job.processing_time && (
                          <div>
                            <span className="font-medium">Processing:</span> {job.processing_time.toFixed(1)}s
                          </div>
                        )}
                        {job.file_size && (
                          <div>
                            <span className="font-medium">Size:</span> {formatFileSize(job.file_size)}
                          </div>
                        )}
                      </div>

                      {job.error_message && (
                        <div className="mt-2 text-xs text-red-700 bg-red-100 rounded p-2">
                          <span className="font-medium">Error:</span> {job.error_message}
                        </div>
                      )}

                      {/* Transcript Section - Only show for completed jobs */}
                      {job.status === 'completed' && job.transcript_text && (
                        <div className="mt-3 border-t pt-3">
                          <button
                            onClick={() => toggleTranscript(job.job_id)}
                            className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900"
                          >
                            <span className="flex items-center">
                              <i className={`fas fa-${expandedTranscripts[job.job_id] ? 'chevron-down' : 'chevron-right'} mr-2`}></i>
                              View Transcript ({job.text_length} characters)
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                copyTranscript(job.transcript_text);
                              }}
                              className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded flex items-center"
                            >
                              <i className="fas fa-copy mr-1"></i>
                              Copy
                            </button>
                          </button>

                          {expandedTranscripts[job.job_id] && (
                            <div className="mt-2 bg-white rounded p-3 border text-xs text-gray-700 max-h-64 overflow-y-auto whitespace-pre-wrap">
                              {job.transcript_text}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TranscriptionService;
